import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authClient } from "@/lib/auth-client";
import { toggleFavorite } from "@/lib/favorites";
import { client } from "@/orpc/client";

const QUERY_KEY = ["favorites"] as const;
type FavData = { favorites: string[] };

/**
 * Signed-in favorites with optimistic toggling. Returns an empty, no-op result
 * for anonymous users so callers can render unconditionally.
 */
export function useFavorites() {
  const { data: session } = authClient.useSession();
  const signedIn = !!session?.user;
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => client.favorites.list({}),
    enabled: signedIn,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (toolId: string) => client.favorites.toggle({ toolId }),
    onMutate: async (toolId: string) => {
      await qc.cancelQueries({ queryKey: QUERY_KEY });
      const prev = qc.getQueryData<FavData>(QUERY_KEY);
      qc.setQueryData<FavData>(QUERY_KEY, (old) => ({
        favorites: toggleFavorite(old?.favorites ?? [], toolId),
      }));
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(QUERY_KEY, ctx.prev);
    },
    onSuccess: (data) => qc.setQueryData(QUERY_KEY, data),
  });

  const favorites = query.data?.favorites ?? [];
  const set = new Set(favorites);

  return {
    signedIn,
    favorites,
    isFavorite: (id: string) => set.has(id),
    toggle: (id: string) => mutation.mutate(id),
  };
}
